import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Table from '@risksmart-app/components/src/table';
import { type FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import ExportButton from 'src/components/export-button';
import { PageLayout } from 'src/layouts';
import { Permission } from 'src/rbac/Permission';
import { useHasPermissionQuery } from 'src/rbac/useHasPermission';

import { useGetCustomDatasources } from '@/hooks/queries';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { getCounter } from '@/utils/collectionUtils';
import { addCustomDatasourceUrl } from '@/utils/urls';

import { useGetCollectionTableProps } from './config';
import { CustomDatasourceRegisterActions } from './CustomDatasourceRegisterActions';
import { CustomDatasourceRegisterToolbar } from './CustomDatasourceRegisterToolbar';
import { CUSTOM_DATASOURCE_SMART_COPY } from './customDatasourceSmartCopy';
import type { CustomDatasourceTableFields } from './types';

const Page: FC = () => {
  const { t: st } = useTranslation(['common'], {
    keyPrefix: 'customDatasources',
  });
  const objectToolbarEnabled = useIsFeatureFlagEnabled('object_toolbar');
  const navigate = useNavigate();

  const { data, loading } = useGetCustomDatasources({ queryArgs: {} });

  const tableProps = useGetCollectionTableProps(data?.custom_datasource);

  const title = st('register_title');
  const counter = getCounter(tableProps.totalItemsCount, loading);
  const openCreate = useCallback(() => {
    navigate(addCustomDatasourceUrl());
  }, [navigate]);

  // Reads OpenFeature (flagd locally/eph, Datadog on dev-cloud/prod), or the
  // seeded Meta.features override for e2e test orgs.
  const smartCopyFlagEnabled = useIsFeatureFlagEnabled(
    CUSTOM_DATASOURCE_SMART_COPY.flagKey
  );

  // When on, the Add/Copy dropdown lives inline on the table tools row
  // (mirroring the Indicator / Control-Test registers) so it shows regardless of
  // the object toolbar, which is left untouched. Off leaves the register unchanged.
  const isSmartCopyEnabled = smartCopyFlagEnabled;

  const { hasPermission: canInsert, loading: canInsertLoading } =
    useHasPermissionQuery('insert:custom_datasource');

  // Multi-select, matching the sister Smart Copy registers. "Copy" needs exactly
  // one source, so it disables at 0 or 2+ rather than the table restricting
  // selection. Insert-gated because Copy is selection's only consumer today.
  const [selected, setSelected] = useState<CustomDatasourceTableFields[]>([]);
  const showSelection = isSmartCopyEnabled && canInsert && !canInsertLoading;

  return (
    <PageLayout
      helpTranslationKey={'customDatasources.registerHelp'}
      title={title}
      counter={counter}
      // Object-level toolbar (CUS-172) — flag-gated. Mounted only when the flag
      // is on, so `useExportOptions` runs on exactly one path (toolbar vs the
      // legacy ExportButton) rather than both.
      headerOverride={
        objectToolbarEnabled ? (
          <CustomDatasourceRegisterToolbar
            title={title}
            counter={counter}
            tableProps={tableProps}
            onCreate={openCreate}
          />
        ) : undefined
      }
      actions={
        <SpaceBetween direction={'horizontal'} size={'xs'}>
          <ExportButton tableProps={tableProps} entityLabel={title} />
          {/* Smart Copy off → the register's plain Add stays here, unchanged.
              On → the Add/Copy dropdown moves onto the table tools row below.
              This leg renders a plain Add button, so it carries no selection. */}
          {!isSmartCopyEnabled && (
            <Permission permission={'insert:custom_datasource'}>
              <CustomDatasourceRegisterActions
                isSmartCopyEnabled={false}
                selectedCount={0}
              />
            </Permission>
          )}
        </SpaceBetween>
      }
      meta={{
        title: st('register_title'),
      }}
    >
      <Table
        {...tableProps}
        loading={loading}
        // Smart-copy on → wrap the filter slot so the Add/Copy dropdown sits
        // inline on the tools row, beside the filter and pagination.
        filter={
          isSmartCopyEnabled ? (
            <div className={'flex flex-1 items-start gap-3'}>
              <div className={'flex-1'}>{tableProps.filter}</div>
              <Permission permission={'insert:custom_datasource'}>
                <CustomDatasourceRegisterActions
                  isSmartCopyEnabled
                  selectedCount={selected.length}
                  // Only ever the sole selection — never the first of many.
                  selectedId={
                    selected.length === 1 ? selected[0].Id : undefined
                  }
                />
              </Permission>
            </div>
          ) : (
            tableProps.filter
          )
        }
        selectionType={showSelection ? 'multi' : undefined}
        selectedItems={selected}
        trackBy={'Id'}
        onSelectionChange={({ detail }) => setSelected(detail.selectedItems)}
      />
    </PageLayout>
  );
};

export default Page;
